import { useState, useMemo, useCallback } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import { pdf } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';

import SloganCard from './components/SloganCard';
import { SloganGeneratorPDF } from './components/SloganGeneratorPDF';
import * as Styled from './SloganGeneratorResults.style';
import { SloganGeneratorResultsData } from './types';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import ListPanel from '../ReportLayout/ListPanel';
import ProfilePanel from '../ReportLayout/ProfilePanel';
import SidePanel from '../ReportLayout/SidePanel';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';

/**
 * Slogan Generator Results component
 * Displays generated slogans with selection and PDF export capabilities
 * Uses shared ReportLayout components for consistent UI pattern
 *
 * @param {object} props - Component props
 * @param {object} props.data - Slogan generator results data
 * @param {Array} [props.actions=[]] - Array of action types (e.g., 'generate_more', 'start_over')
 * @param {Function} [props.onComponentClick] - Callback for component actions
 * @component
 */
function SloganGeneratorResults({ data, actions = [], onComponentClick = null }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const [loadingAction, setLoadingAction] = useState(null);
  const [checkedSlogans, setCheckedSlogans] = useState(new Set());

  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'SloganGeneratorResults'
    }
  });

  // Memoized values
  const slogans = useMemo(() => data?.slogans || [], [data?.slogans]);

  // handleAction factory pattern (aligned with MarketAnalysisReport)
  const handleAction = async (actionType) => {
    if (loadingAction) {
      return;
    }
    setLoadingAction(actionType);
    try {
      await onComponentClick?.({ type: actionType, payload: { data } });
    } catch (error) {
      captureException(error, {
        level: 'error',
        context: { action: actionType }
      });
    } finally {
      setLoadingAction(null);
    }
  };

  const handleGenerateMore = async () => {
    await handleAction('generate_more');
  };

  const handleStartOver = async () => {
    await handleAction('start_over');
  };

  // Slogan checkbox handler
  const handleCheckChange = useCallback((sloganId, isChecked) => {
    setCheckedSlogans((prev) => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(sloganId);
      } else {
        next.delete(sloganId);
      }
      return next;
    });
  }, []);

  // PDF export handler (client-side, specific to slogan generator)
  const handleExportPDF = useCallback(async () => {
    if (loadingAction) return;

    setLoadingAction('export_pdf');

    try {
      // Get slogans to include in PDF - only checked ones, or all if none checked
      const slogansToExport =
        checkedSlogans.size > 0 ? slogans.filter((s) => checkedSlogans.has(s.id)) : slogans;

      const blob = await pdf(
        <SloganGeneratorPDF
          environment={DEPLOY_ENV}
          slogans={slogansToExport}
          userInput={data?.userInput}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = data?.userInput?.businessName
        ? `${data.userInput.businessName.replace(/\s+/g, '_')}_slogans.pdf`
        : 'slogans.pdf';
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      captureException(error, {
        level: 'error',
        context: { action: 'export_pdf' }
      });
    } finally {
      setLoadingAction(null);
    }
  }, [loadingAction, checkedSlogans, slogans, data?.userInput, DEPLOY_ENV, captureException]);

  // Profile fields for ProfilePanel (business details)
  const profileFields = useMemo(() => {
    const userInput = data?.userInput;
    if (!userInput) return [];

    return [
      userInput.businessName && { label: 'Business Name', value: userInput.businessName },
      userInput.industry && { label: 'Industry', value: userInput.industry },
      userInput.location && { label: 'Location', value: userInput.location },
      userInput.description && { label: 'Description', value: userInput.description },
      userInput.uniqueSellingPoint && {
        label: 'Unique Selling Point',
        value: userInput.uniqueSellingPoint
      },
      userInput.brandPersonality && {
        label: 'Brand Personality',
        value: userInput.brandPersonality
      },
      userInput.preferredStyle && { label: 'Preferred Style', value: userInput.preferredStyle }
    ].filter(Boolean);
  }, [data?.userInput]);

  // Empty state
  if (!slogans || slogans.length === 0) {
    return (
      <Styled.ReportRoot>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Text size="lg" weight="medium">
            No slogans generated
          </Text>
          <Text color="secondary" size="md">
            We weren&apos;t able to generate slogans based on your responses. Please try again.
          </Text>
          {actions.includes('start_over') && (
            <Box mt={3}>
              <Button color="primary" variant="contained" onClick={handleStartOver}>
                Start Over
              </Button>
            </Box>
          )}
        </Box>
      </Styled.ReportRoot>
    );
  }

  const showGenerateMore = actions.includes('generate_more');
  const showStartOver = actions.includes('start_over');

  return (
    <Styled.ReportRoot>
      {/* Header */}
      <Text size="md" weight="medium">
        Your custom slogans crafted based on your brand personality and inputs
      </Text>

      <Styled.ReportContent>
        {/* Left Panel - Business Details & Actions */}
        <SidePanel
          profilePanel={
            profileFields.length > 0 ? (
              <ProfilePanel fields={profileFields} loading={!!loadingAction} />
            ) : null
          }
        >
          {/* Action Buttons */}
          {(showGenerateMore || showStartOver) && (
            <Styled.ButtonGroup>
              {showGenerateMore && (
                <Button
                  clickstreamProps={{
                    ...CLICKSTREAM_COMMON_PROPS,
                    elementName: 'slogan_generator_generate_more',
                    elementText: 'Generate more slogans'
                  }}
                  color="info"
                  disabled={!!loadingAction}
                  fullWidth
                  loading={loadingAction === 'slogan_generator_generate_more'}
                  size="medium"
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  onClick={handleGenerateMore}
                >
                  Generate more slogans
                </Button>
              )}
              {showStartOver && (
                <Button
                  clickstreamProps={{
                    ...CLICKSTREAM_COMMON_PROPS,
                    elementName: 'slogan_generator_start_over',
                    elementText: 'Start over'
                  }}
                  disabled={!!loadingAction}
                  fullWidth
                  loading={loadingAction === 'slogan_generator_start_over'}
                  size="medium"
                  variant="outlined"
                  onClick={handleStartOver}
                >
                  Start over
                </Button>
              )}
            </Styled.ButtonGroup>
          )}
        </SidePanel>

        {/* Right Panel - Slogans List using ListPanel */}
        <ListPanel
          clickstreamPrefix="slogan_generator"
          exportButtonText="Export selected slogans"
          getItemId={(slogan) => slogan.id}
          items={slogans}
          loading={!!loadingAction}
          renderItem={(slogan, { checked, onCheckChange }) => (
            <SloganCard
              key={slogan.id}
              checked={checked}
              clickstreamElementName="slogan_generator_copy"
              showCopy
              slogan={slogan}
              onCheckChange={(id, isChecked) => onCheckChange(isChecked)}
            />
          )}
          selectedIds={checkedSlogans}
          showBottomExportButton
          onExport={handleExportPDF}
          onSelectionChange={handleCheckChange}
        />
      </Styled.ReportContent>

      {/* Footer CTA */}
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.ReportRoot>
  );
}

SloganGeneratorResults.propTypes = {
  data: SloganGeneratorResultsData,
  actions: PropTypes.arrayOf(PropTypes.string),
  onComponentClick: PropTypes.func
};

export default SloganGeneratorResults;
